<template>
  <div class="product-pages-component">
    <ProductPages
      :is-product-segmentation="true"
      :segmented-products="selectedProducts"
      @remove-product-segmentation="removeProduct"
    />
    <div class="locales-container">
      <template v-if="hasLanguageSettings && selectedLanguages.length === 1">
        <button class="locale-selected" @click="addLanguage">
          <span
            >{{ $t("urlSegmentation.localeIs") }}
            {{ getLocaleLabel(currentLanguageKey) }}</span
          >
          <img :src="currentLocale.flag" alt="locale-flag" class="flag-icon" />
          <SquaredX class="remove-button" @click.stop="removeLanguage" />
        </button>
      </template>
      <template v-else>
        <button
          v-if="hasLanguageSettings"
          class="page-exclusion locale-button"
          @click="addLanguage"
        >
          {{ $t("urlSegmentation.locales") }}
          <LocalIcon class="locale-icon" />
        </button>
      </template>
    </div>
    <div
      v-if="displayUrlRules || inclusionCondition || exclusionCondition"
      class="url-rules"
    >
      <div class="inclusion">
        <button
          v-if="!inclusionCondition"
          class="page-exclusion"
          @click="addInclusion"
        >
          {{ $t("urlSegmentation.inclusion") }}
        </button>
        <SegmentationConditions
          v-else
          :data-type="'URL'"
          :condition="inclusionCondition"
          :index="inclusionIndex"
          :is-url-segmentation="true"
          :is-url-inclusion="true"
          @updateCondition="updateInclusionCondition"
          @removeCondition="removeInclusionCondition"
        />
        <SegmentationFilterDropdown
          v-if="displayInclusionDropdown"
          class="inclusion-dropdown"
          :is-first-url-segmentation="true"
          :filter="filterForDropdown"
          :first-filter="firstFilter"
          :is-url-inclusion="isUrlInclusion"
          :is-url-exclusion="isUrlExclusion"
          @close="confirmRule"
        />
      </div>
      <button v-if="shouldShowLogicToggle()" class="logic-toggle">
        {{ globalOperator }}
      </button>
      <div class="exclusion">
        <button
          v-if="!exclusionCondition"
          class="page-exclusion"
          @click="addExclusion"
        >
          {{ $t("urlSegmentation.exclusion") }}
        </button>
        <SegmentationConditions
          v-else
          :data-type="'URL'"
          :condition="exclusionCondition"
          :index="exclusionIndex"
          :is-url-segmentation="true"
          :is-url-exclusion="true"
          @updateCondition="updateExclusionCondition"
          @removeCondition="removeExclusionCondition"
        />
        <SegmentationFilterDropdown
          v-if="displayExclusionDropdown"
          class="exclusion-dropdown"
          :is-first-url-segmentation="true"
          :filter="filterForDropdown"
          :first-filter="firstFilter"
          :is-url-inclusion="isUrlInclusion"
          :is-url-exclusion="isUrlExclusion"
          @close="confirmRule"
        />
      </div>
    </div>
    <div
      :class="[
        'buttons',
        { 'sticky-buttons': isSticky },
        {
          'buttons-with-margin-top':
            displayUrlRules ||
            (languageSegmentation && languageSegmentation.length > 0),
        },
      ]"
    >
      <button class="add-product-button" @click="toggleProductsModal">
        <TagIcon />
        {{ $t("create.bulk.pagesModal.addProduct") }}
      </button>
      <button
        class="add-product-button add-url-button"
        @click="showAddUrlRules"
      >
        <LinkIcon class="link-icon" />{{ $t("urlSegmentation.mainButton") }}
      </button>
    </div>
    <div class="bottom-page"></div>
    <div class="scroll-here"></div>
    <AddToCartModal
      v-if="showProductsModal"
      :is-full-screen="true"
      :selected-products-edit="selectedProducts"
      :products-loading="productsLoading"
      @close-add-to-cart="showProductsModal = false"
      @submit-add-to-cart-products="submitAddToCartProducts"
    />
    <PickSegmentationLanguageModal
      v-if="showLanguagePicker"
      :languages-options="languagesOptions"
      :selected-languages="selectedLanguages"
      @filter="applyLanguageFilter"
      @close-filter-modal="showLanguagePicker = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import AddToCartModal from "@/components/create-campaign-steps/step3/AddToCartModal.vue";
import ProductPages from "@/components/ProductPages.vue";
import SegmentationConditions from "@/components/shared/SegmentationConditions.vue";
import TagIcon from "@/assets/svg/tag-icon.svg?inline";
import LinkIcon from "@/assets/svg/link-icon.svg?inline";
import SegmentationFilterDropdown from "@/components/shared/SegmentationFilterDropdown.vue";
import LocalIcon from "@/assets/svg/local-icon.svg?inline";
import PickSegmentationLanguageModal from "@/components/shared/PickSegmentationLanguageModal.vue";
import { locales } from "@/utils/locales";
import SquaredX from "@/assets/svg/squared-x-icon.svg?inline";

export default {
  components: {
    AddToCartModal,
    ProductPages,
    TagIcon,
    LinkIcon,
    SegmentationConditions,
    SegmentationFilterDropdown,
    LocalIcon,
    PickSegmentationLanguageModal,
    SquaredX,
  },
  props: {
    productIds: { type: Array, default: () => [] },
    urlSegmentation: { type: Array, default: () => [] },
    languageSegmentation: { type: Array, default: () => [] },
  },
  data() {
    return {
      showProductsModal: false,
      productsLoading: false,
      conditions: [],
      globalOperator: "and",
      firstFilter: null,
      displayUrlRules: false,
      isUrlInclusion: false,
      isUrlExclusion: false,
      inclusionCondition: null,
      exclusionCondition: null,
      filterForDropdown: null,
      displayInclusionDropdown: false,
      displayExclusionDropdown: false,
      isSticky: false,
      showLanguagePicker: false,
      selectedLanguages: this.languageSegmentation.slice(),
      locales,
    };
  },
  computed: {
    ...mapState(["site"]),
    ...mapState({
      products: (state) => state.product.products,
      selectedProducts: (state) => state.product.selectedProducts,
    }),
    ...mapGetters({
      getCurrency: "site/getCurrency",
      isUsingProductKey: "site/isUsingProductKey",
      siteId: "account/getSiteId",
      currentFeedEdit: "campaign/currentFeedEdit",
      feedList: "campaign/feedList",
    }),
    productListHeaders() {
      return [
        this.$t("create.bulk.pagesModal.listItems.productPages") +
          this.productIds.length,
        this.$t(""),
        this.$t(""),
        this.$t(""),
        this.$t("create.bulk.pagesModal.listItems.inventory"),
        this.$t("create.bulk.pagesModal.listItems.price"),
      ];
    },
    lastCondition() {
      return this.conditions[this.conditions.length - 1] || {};
    },
    newInclusionCondition() {
      return (
        this.conditions.find(
          (condition) =>
            condition.filters[0]?.comparison === "eq" ||
            condition.filters[0]?.comparison === "contains"
        ) || null
      );
    },
    newExclusionCondition() {
      return (
        this.conditions.find(
          (condition) =>
            condition.filters[0]?.comparison === "not_eq" ||
            condition.filters[0]?.comparison === "not_contains"
        ) || null
      );
    },
    inclusionIndex() {
      return this.conditions.findIndex(
        (condition) =>
          condition.filters[0]?.comparison === "eq" ||
          condition.filters[0]?.comparison === "contains"
      );
    },
    exclusionIndex() {
      return this.conditions.findIndex(
        (condition) =>
          condition.filters[0]?.comparison === "not_eq" ||
          condition.filters[0]?.comparison === "not_contains"
      );
    },
    languagesOptions() {
      return Object.keys(this.site.site.languageSettings).map((langKey) => {
        return {
          language: langKey,
          label: this.getLocaleLabel(langKey),
        };
      });
    },
    currentLanguageKey() {
      return this.selectedLanguages[0] || "";
    },
    currentLocale() {
      return (
        this.locales.find((l) => l.code === `/${this.currentLanguageKey}/`) || {
          name: "",
          flag: "",
        }
      );
    },
    hasLanguageSettings() {
      const ls = this.site.site.languageSettings;
      return ls != null && Object.keys(ls).length > 0;
    },
    currentLang() {
      return this.$i18n.locale.split("-")[0];
    },
  },
  watch: {
    inclusionCondition: {
      deep: true,
      handler(newVal) {
        this.refactorUrlSegmentation();
      },
    },
    exclusionCondition: {
      deep: true,
      handler(newVal) {
        this.refactorUrlSegmentation();
      },
    },
    languageSegmentation(newVal) {
      this.selectedLanguages = newVal.slice();
    },
    async productIds(productIds) {
      if (!productIds.length)
        return this.$store.commit("product/resetSelectedProducts");

      await this.$store.dispatch("product/getSpecificProducts", {
        siteId: this.siteId,
        query: { productIds: productIds },
        isProductListView: true,
      });
    },
  },
  async created() {
    //Retreive the list of products already segmented
    if (this.productIds?.length) {
      await this.$store.dispatch("product/getSpecificProducts", {
        siteId: this.siteId,
        query: { productIds: this.productIds },
        isProductListView: true,
      });
    } else this.$store.commit("product/resetSelectedProducts");

    let inclusionCondition = { filters: [], type: "or" };
    let exclusionCondition = { filters: [], type: "and" };

    if (this.urlSegmentation && this.urlSegmentation.length > 0) {
      this.displayUrlRules = true;
      this.urlSegmentation.forEach((condition) => {
        if (condition.filters) {
          condition.filters.forEach((filterGroup) => {
            if (filterGroup.filters && filterGroup.filters.length > 1) {
              const filters = filterGroup.filters.map((filter) => {
                const { isEditing, ...rest } = filter;
                return rest;
              });

              filters.forEach((filter) => {
                if (
                  filter.comparison === "eq" ||
                  filter.comparison === "contains"
                ) {
                  inclusionCondition.filters.push({
                    ...filter,
                    isEditing: false,
                    isInclusion: true,
                    type: "or",
                  });
                }

                if (
                  filter.comparison === "not_eq" ||
                  filter.comparison === "not_contains"
                ) {
                  exclusionCondition.filters.push({
                    ...filter,
                    isEditing: false,
                    isExclusion: true,
                    type: "and",
                  });
                }
              });
            } else {
              const filter = filterGroup;
              if (
                filter.comparison === "eq" ||
                filter.comparison === "contains"
              ) {
                inclusionCondition.filters.push({
                  ...filter,
                  isEditing: false,
                  isInclusion: true,
                  type: "or",
                });
              }

              if (
                filter.comparison === "not_eq" ||
                filter.comparison === "not_contains"
              ) {
                exclusionCondition.filters.push({
                  ...filter,
                  isEditing: false,
                  isExclusion: true,
                  type: "and",
                });
              }
            }
          });
        } else {
          const filter = condition;
          if (filter.comparison === "eq" || filter.comparison === "contains") {
            inclusionCondition.filters.push({
              ...filter,
              isEditing: false,
              isInclusion: true,
              type: "or",
            });
          }

          if (
            filter.comparison === "not_eq" ||
            filter.comparison === "not_contains"
          ) {
            exclusionCondition.filters.push({
              ...filter,
              isEditing: false,
              isExclusion: true,
              type: "and",
            });
          }
        }
      });
    }

    if (inclusionCondition.filters.length === 0) {
      this.inclusionCondition = null;
    } else {
      this.inclusionCondition = inclusionCondition;
    }

    if (exclusionCondition.filters.length === 0) {
      this.exclusionCondition = null;
    } else {
      this.exclusionCondition = exclusionCondition;
    }

    if (!this.inclusionCondition && !this.exclusionCondition) {
      return;
    }
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    calculateTotalQty(productId) {
      const product = this.getProductById(productId);
      if (product && product.variants) {
        let totalQty = product.variants.reduce(
          (total, variant) => total + variant.qty,
          0
        );
        let hasUnlimited = product.variants.some(
          (variant) => variant.sellWithoutStock
        );

        if (hasUnlimited) {
          return this.$t("create.bulk.pagesModal.stock.unlimited");
        } else if (totalQty === 0) {
          return this.$t("create.bulk.pagesModal.stock.outStock");
        } else {
          return totalQty + " " + this.$t("create.bulk.pagesModal.stock.stock");
        }
      }
      return this.$t("create.bulk.pagesModal.stock.outStock");
    },
    firstVariantPrice(product) {
      if (product && product.variants && product.variants.length > 0) {
        const firstVariant = product.variants[0];
        const price = firstVariant ? firstVariant.price : 0;

        if (this.getCurrency === "$") {
          return `$${price}`;
        } else if (this.getCurrency === "€") {
          return `${price}€`;
        } else {
          return `${price}${this.getCurrency}`;
        }
      } else {
        return "";
      }
    },
    getProductById(productId) {
      //For client trois kilos sept, we are using the product key
      if (this.isUsingProductKey)
        return this.products.find(
          (product) => product.productKey === productId
        );

      return this.products.find(
        (product) =>
          product.productId === String(productId) ||
          product.productId === parseInt(productId)
      );
    },
    removeProduct(productId) {
      this.$emit(
        "update-product-segmentation",
        this.productIds.filter((id) => id !== productId)
      );
    },
    toggleProductsModal() {
      this.showProductsModal = !this.showProductsModal;
    },
    submitAddToCartProducts(payload) {
      this.productsLoading = true;
      const productIds = payload.map((product) =>
        this.isUsingProductKey ? product.productKey : product.productId
      );

      this.$emit("update-product-segmentation", productIds);

      this.productsLoading = false;
      this.showProductsModal = false;

      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("create.bulk.pagesModal.productAdded"),
        type: "success",
      });
    },
    addInclusion() {
      if (this.displayInclusionDropdown === true) {
        return;
      }
      this.isUrlInclusion = true;

      if (!this.inclusionCondition) {
        this.inclusionCondition = {
          filters: [
            {
              comparison: "eq",
              isEditing: false,
              string: "",
              dataType: "URL",
              isInclusion: true,
            },
          ],
          type: "or",
        };
      }
      this.firstFilter = {
        isInclusion: true,
      };

      this.filterForDropdown = this.inclusionCondition.filters[0];

      this.displayInclusionDropdown = true;
    },
    addExclusion() {
      if (this.displayExclusionDropdown === true) {
        return;
      }

      this.isUrlExclusion = true;

      if (!this.exclusionCondition) {
        this.exclusionCondition = {
          filters: [
            {
              comparison: "not_eq",
              isEditing: false,
              string: "",
              dataType: "URL",
              isExclusion: true,
            },
          ],
          type: "and",
        };
      }

      this.firstFilter = {
        isExclusion: true,
      };

      this.filterForDropdown = this.exclusionCondition.filters[0];
      this.displayExclusionDropdown = true;
    },
    updateInclusionCondition(index, newCondition) {
      this.inclusionCondition = newCondition;
    },
    updateExclusionCondition(index, newCondition) {
      this.exclusionCondition = newCondition;
    },
    removeInclusionCondition() {
      this.inclusionCondition = null;
    },
    removeExclusionCondition() {
      this.exclusionCondition = null;
    },
    confirmRule() {
      this.displayInclusionDropdown = false;
      this.displayExclusionDropdown = false;
      this.isUrlInclusion = false;
      this.isUrlExclusion = false;
    },
    shouldShowLogicToggle() {
      return (
        this.inclusionCondition !== null && this.exclusionCondition !== null
      );
    },
    showAddUrlRules() {
      this.displayUrlRules = true;
      this.$nextTick(() => {
        const element = document.querySelector(".scroll-here");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      });
    },
    refactorUrlSegmentation() {
      const combinedCondition = { url: [] };

      const extractFilters = (cond, groupType) => {
        if (!cond || !cond.filters?.length)
          return { filters: [], allFilled: true };
        const cleaned = cond.filters.map(
          ({ isEditing, isInclusion, isExclusion, dataType, type, ...rest }) =>
            rest
        );
        const allFilled = cond.filters.every((f) => {
          return (
            !Object.prototype.hasOwnProperty.call(f, "string") ||
            (f.string && f.string.trim().length > 0)
          );
        });
        if (cleaned.length === 1) return { filters: [cleaned[0]], allFilled };
        return { filters: [{ filters: cleaned, type: groupType }], allFilled };
      };

      const inc = extractFilters(this.inclusionCondition, "or");
      const exc = extractFilters(this.exclusionCondition, "and");

      combinedCondition.url.push(...inc.filters, ...exc.filters);

      const payloadUrl =
        combinedCondition.url.length > 1
          ? { url: [{ filters: combinedCondition.url, type: "and" }] }
          : { url: combinedCondition.url };

      const areAllInputsFilled = inc.allFilled && exc.allFilled;

      this.$emit("update-url-segmentation", {
        ...payloadUrl,
        areAllInputsFilled,
      });
    },
    handleScroll() {
      const buttons = document.querySelector(".buttons");
      const buttonsRect = buttons.getBoundingClientRect();
      const offset = 50;

      if (buttonsRect.bottom > window.innerHeight - offset) {
        this.isSticky = true;
      } else {
        this.isSticky = false;
      }
    },
    addLanguage() {
      this.showLanguagePicker = true;
    },
    applyLanguageFilter({ languages }) {
      this.selectedLanguages = languages;
      this.$emit("update-language-segmentation", { language: languages });
      this.showLanguagePicker = false;
    },
    removeLanguage() {
      this.selectedLanguages = [];
      this.$emit("update-language-segmentation", { language: [] });
    },
    getLocaleLabel(codeKey) {
      const locale =
        locales.find((l) => l.code.replace(/\//g, "") === codeKey) || {};
      return (locale.name && locale.name[this.currentLang]) || locale.name.en;
    },
  },
};
</script>

<style lang="scss" scoped>
.add-product-button {
  @include base-font;
  @include font-smaller;
  padding: 8px 24px;
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: $dark-purple;
  color: white;
  font-weight: 500;
  border-radius: 3px 0 0 3px;
}
.buttons {
  display: flex;
  align-items: center;
  position: sticky;
  bottom: 50px;
  z-index: 20;
}

.delete-icon {
  position: absolute;
  right: 16px;
  cursor: pointer;
}
.add-url-button {
  background-color: rgba(234, 234, 234, 1);
  color: $dark-grey;
  border-radius: 0 3px 3px 0;
  .link-icon {
    path {
      fill: $dark-grey;
    }
  }
}
.logic-toggle {
  @include base-font;
  @include font-smaller;
  margin-bottom: 10px;
  height: 32px;
  border-radius: 2px;
}

.page-exclusion {
  @include base-font;
  @include font-smaller;
  font-weight: 600;
  color: $dark-purple;
  background: transparent;
}

.url-rules {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.inclusion .exclusion {
  position: relative;
}
.inclusion-dropdown {
  left: 0;
  bottom: 138px;
}

.exclusion-dropdown {
  left: 0;
  bottom: 113px;
}
.sticky-buttons {
  position: fixed;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  padding: 10px;
  box-shadow: 0px -2px 5px rgba(0, 0, 0, 0.1);
}
.bottom-page {
  height: 30px;
}

.buttons-with-margin-top {
  margin-top: 24px;
}
.locale-icon {
  transform: scale(0.7);
}
.locale-button {
  display: flex;
  align-items: center;
}
.flag-icon {
  width: 16px;
  height: 16px;
}
.locale-selected {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  height: 32px;
  box-sizing: border-box;
  font-weight: 500;
  align-items: center;
  padding: 8px 12px;
  background-color: rgb(234, 234, 234);
  border-radius: 2px;
  position: relative;
  max-width: 350px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  gap: 4px;
  cursor: pointer;
  &:hover {
    background-color: rgba(224, 224, 224, 1);
    .remove-button {
      display: flex;
      background-color: rgba(224, 224, 224, 1);
    }
  }
}
.remove-button {
  position: absolute;
  right: 0%;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(234, 234, 234, 1);
  cursor: pointer;
  display: none;
  align-items: center;
  padding: 2px 8px;
  z-index: 100;
  &::before {
    content: "";
    position: absolute;
    left: -8px;
    top: 0;
    bottom: 0;
    width: 8px;
    background-color: rgba(234, 234, 234, 1);
    filter: blur(6px);
    z-index: -1;
  }
}
.locales-container {
  margin-bottom: 6px;
}
</style>
